import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./index.module.scss";
import { endPoints } from "../../constants/urlConstants";
import { getCall } from "../../services/apiService";
import { getItemInLocalStorage } from "../../services/localStorage";
import { hasPermission } from "../../utils/roleBasedAccess";
import { swapTypeOptions } from "../../constants";
import starIcon from "../../assets/images/starIcon.svg";
// Components
import DataGridWithPagination from "../../common/components/DataGridWithPagination";
import Loader from "../../common/components/Loader";
import DashboardHeader from "../../common/components/DashboardHeader";
import AdminFilterOverlay from "../../components/components/AdminFilterOverlay";
import AdminKpiData from "../../common/components/AdminKpiData";
import ReviewOverlay from "../../components/RatingOverLay";
import SwapFilterOverlay from "../../common/components/SwapFilterContent";
import KebabMenu from "../../common/components/KebabMenu";
import FilterPills from "../filterItemsDisplay";
import chatTearDrop from "../../assets/images/ChatTeardropDots.svg";
import defaultProfileIcon from "../../assets/images/default-profile.svg";

// Types
type HdbSeekersListAdminProps = {
  filters?: any;
};
import MahatriaBlessCard from "../../common/components/MahatriaBlessCard";
import { ApiService } from "../../services/mockService";
import { Tooltip } from "@mui/material";
import DownloadPopup from "../DownloadPopUp";
import { downloadExcelFromApi } from "../../utils/downloadExcel";
import { downloadPdf } from "../../utils/downloadPdf";

const HdbSeekersListAdmin: React.FC<HdbSeekersListAdminProps> = (props) => {
  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const { programId } = useParams<{ programId: string }>();
  const programIdNumber = programId ? Number(programId) : undefined;
  const sessionId = location.state?.sessionId ?? undefined;
  const { filters } = props;

  // State variables
  const userRole = getItemInLocalStorage("seekerDetails")?.role || "";

  // Extract kpiCategory and kpiFilter if present
  const initialKpiFilter =
    filters && filters.kpiCategory && filters.kpiFilter
      ? { kpiCategory: filters.kpiCategory, kpiFilter: filters.kpiFilter }
      : null;

  // Remove kpiCategory and kpiFilter from appliedFilters if present
  const initialAppliedFilters =
    filters && typeof filters === "object"
      ? Object.keys(filters).reduce((acc, key) => {
          if (key !== "kpiCategory" && key !== "kpiFilter") {
            acc[key] = filters[key];
          }
          return acc;
        }, {} as any)
      : {};

  const [selectedKpiFilter, setSelectedKpiFilter] = useState(initialKpiFilter);
  const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);

  const [data, setData] = useState<any[]>([]);
  const [dashboardFilters, setDashboardFilters] = useState<any>({});
  const [columns, setColumns] = useState<any[]>([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState({ value: "", open: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [activeOverlayType, setActiveOverlayType] = useState<
    "review" | "swap" | null
  >(null);
  const [dynamicProgramOptions, setDynamicProgramOptions] = useState<any[]>([]);
  const [reviewInitialRatings, setReviewInitialRatings] = useState<{
    [key: string]: { rating: number; id?: number };
  }>({});
  const [reviewInitialComments, setReviewInitialComments] = useState("");
  const [kpis, setKpis] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [hasSyncedTab, setHasSyncedTab] = useState(false);
  const [subProgramsData, setSubProgramsData] = useState([]);

  // State for export popup
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [selectedOption, setSelectedOption] = useState<"filtered" | "all">(
    "filtered",
  );
  useEffect(() => {
    setSearchValue((prev) => ({ ...prev, open: false, value: "" }));
  }, [activeTab, appliedFilters, currentPage]);

  useEffect(() => {
    if (!programId) return;
    getSubProgramsData();
  }, [programId]);

  const getSubProgramsData = async () => {
    try {
      const res = await getCall(`program/${programId}`);
      const data = res?.data?.data?.groupedPrograms || [];
      const transformedData = data.map(({ id, name }) => {
        return { id, name };
      });
      setSubProgramsData(transformedData);
    } catch (error) {
      console.error("Error fetching sub-programs:", error);
    }
  };


  // Utility Functions
  const fetchAllocatedProgramId = async (seekerId: string | number) => {
    const url = `${endPoints.RegisteredSeekersList}/${seekerId}`;
    const response = await getCall(url);
    return Number(response?.data?.data?.allocatedProgram?.id);
  };
  const handleReject = async (id: any, seekerName: string) => {
    if (
      !window.confirm(`Are you sure you want to move ${seekerName} to on-hold?`)
    )
      return;
    const payload = {
      registrationId: Number(id),
      approvalStatus: "rejected",
      rejectionReason: "",
      updatedBy: getItemInLocalStorage("seekerDetails")?.id,
    };

    try {
      setLoading(true);
      const response = await ApiService.blessUser(id, payload);
      console.log("Reject API response:", response);
      if (response.data.statusCode === 200) {
        fetchSeekersList();
      } else {
        alert(response.data.message || "Failed to hold user");
      }
    } catch (error) {
      alert("Failed to hold user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleHold = async (id: any, seekerName: string) => {
    if (!window.confirm(`Are you sure you want to move ${seekerName} to YTD?`))
      return;
    const payload = {
      registrationId: Number(id),
      approvalStatus: "on_hold",
      rejectionReason: "",
      updatedBy: getItemInLocalStorage("seekerDetails")?.id,
    };

    try {
      setLoading(true);
      const response = await ApiService.blessUser(id, payload);
      console.log("Hold API response:", response);
      if (response.data.statusCode === 200) {
        fetchSeekersList();
      } else {
        alert(response.data.message || "Failed to move user to YTD");
      }
    } catch (error) {
      alert("Failed to move user to Ytd. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getOptions = (
    id: string,
    row: any,
    onAddReview?: (row: any) => void,
    onSwap?: (row: any) => void,
    onInvoiceSend?: (seekerId: any) => void,
    disabled?: boolean,
  ) => {
    // Extract preferred programs for this row/user
    const preferredPrograms = (row.preference || [])
      .filter((item: any) => item.preferredProgram)
      .map((item: any) => ({
        id: item.preferredProgram.id,
        name: item.preferredProgram.name,
      }));
    console.log("program", preferredPrograms);

    console.log("row object:", row);
    const userRole = getItemInLocalStorage("seekerDetails")?.role || "";

    const options = [
      {
        label: "view details",
        action: () => handleRowClick(row),
        imageSource: undefined,
      },
    ];

    // Add/Update Review
    if (hasPermission(userRole, "ADD_REVIEW", "C")) {
      const hasReview = row.rmComments !== "";
      options.push({
        label: hasReview ? "update review" : "add a review",
        action: () => onAddReview && onAddReview(row),
        imageSource: undefined,
      });
    }

    // Swap Request
    if (
      hasPermission(userRole, "REQ_SWAP_SEEKER", "C") &&
      row.approvalStatus === "approved"
    ) {
      console.log("rowwww", row);

      options.push({
        label: "swap request",
        action: () => onSwap && onSwap(row),
        imageSource: undefined,
        disabled: row.isSwapRequestActive,
      });
    }

    // Send Invoice
    if (
      hasPermission(userRole, "SEND_INVOICE", "C") &&
      row?.paymentStatus?.toLowerCase().includes("completed")
    ) {
      options.push({
        label: "send invoice",
        action: () => onInvoiceSend && onInvoiceSend(row.seekerId || id),
        imageSource: undefined,
      });
      options.push({
        label: "download invoice",
        action: () => downloadInvoice && downloadInvoice(row.invoiceUrl),
        imageSource: undefined,
      });
    }

    // Bless, Reject, Hold (for mahatria)
    if (userRole === "mahatria" || userRole == "shoba") {
      switch (row?.approvalStatus) {
        case "on_hold":
          options.push(
            {
              label: "Bless",
              action: () => {
                setBlessUser(row.seekerName);
                setSelectedRow(row);
                setDynamicProgramOptions(preferredPrograms);
                setShowBlessCard(true);
              },
              imageSource: undefined,
            },
            {
              label: "hold",
              action: () => handleReject(id, row.seekerName),
              imageSource: undefined,
            },
          );
          break;
        case "approved":
          options.push(
            {
              label: "ytd",
              action: () => handleHold(id, row.seekerName),
              imageSource: undefined,
            },
            {
              label: "hold",
              action: () => handleReject(id, row.seekerName),
              imageSource: undefined,
            },
          );
          break;
        case "rejected":
          options.push(
            {
              label: "Bless",
              action: () => {
                setBlessUser(row.seekerName);
                setSelectedRow(row);
                setDynamicProgramOptions(preferredPrograms);
                setShowBlessCard(true);
              },
              imageSource: undefined,
            },
            {
              label: "ytd",
              action: () => handleHold(id, row.seekerName),
              imageSource: undefined,
            },
          );
          break;
        default:
          options.push(
            {
              label: "Bless",
              action: () => {
                setBlessUser(row.seekerName);
                setSelectedRow(row);
                setShowBlessCard(true);
              },
              imageSource: undefined,
            },
            {
              label: "hold",
              action: () => handleReject(id, row.seekerName),
              imageSource: undefined,
            },
            {
              label: "ytd",
              action: () => handleHold(id, row.seekerName),

              imageSource: undefined,
            },
          );
      }
    }

    // Download Proforma Invoice
    if (
      hasPermission(userRole, "DOWNLOAD_PROFORMA", "C") &&
      row.proFormaInvoicePdfUrl
    ) {
      options.push({
        label: "download proforma invoice",
        action: () => {
          downloadPdf(row.proFormaInvoicePdfUrl);
        },
        imageSource: undefined,
      });
    }
    return options;
  };
  const [showBlessCard, setShowBlessCard] = useState(false);
  const [blessUser, setBlessUser] = useState("");

  const buildDynamicColumns = (tableHeaders: any[]) => {
    const filteredHeaders = tableHeaders.filter(
      (header: any) =>
        header.key !== "profileUrl" &&
        header.label?.toLowerCase() !== "profile url" &&
        header.key !== "rmComments" &&
        header.key !== "age" &&
        header.key !== "gender" &&
        header.key !== "location" &&
        header.key !== "mobileNumber",
    );

    const cols = filteredHeaders.map((header: any) => ({
      field: header.key,
      headerName: header.label,
      sortable: header.sortable,
      width: ["rmComments", "paymentStatus"].includes(header.key)
        ? 200
        : ["seekerName", "fullName", "name"].includes(header.key)
          ? 230
          : 130,

      renderCell: (params: any) => {
        const value = params.row[header.key] ?? "-";

        if (header.key === "averageRating") {
          const avg = params.row.averageRating;
          const hasNoRating =
            avg === null ||
            avg === undefined ||
            avg === "" ||
            avg === 0 ||
            avg === "0.00";
          const hasComment =
            params.row.rmComments && params.row.rmComments !== "";

          return (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {hasNoRating ? (
                <>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "47px",
                      height: "28px",
                    }}
                  >
                    -
                  </span>
                  {hasComment && (
                    <Tooltip
                      title={
                        <p className={styles.reviewText}>
                          {params.row.rmComments
                            ? params.row.rmComments.split('\n').map((line: string, idx: number, arr: string[]) => (
                                <React.Fragment key={idx}>
                                  {line}
                                  {idx < arr.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            : ""}
                        </p>
                      }
                    >
                      <img
                        src={chatTearDrop}
                        alt="chat"
                        style={{ marginLeft: 4, cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRow(params.row);
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  <img src={starIcon} alt="star" />
                  <span>{avg}</span>
                  {hasComment && (
                    <Tooltip
                      title={
                        <p className={styles.reviewText}>
                          {params.row.rmComments
                            ? params.row.rmComments.split('\n').map((line: string, idx: number, arr: string[]) => (
                                <React.Fragment key={idx}>
                                  {line}
                                  {idx < arr.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            : ""}
                        </p>
                      }
                    >
                      <img
                        src={chatTearDrop}
                        alt="chat"
                        style={{ marginLeft: 4, cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRow(params.row);
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </span>
          );
        }

        if (["seekerName", "fullName", "name"].includes(header.key)) {
          // Truncate name if longer than 22 chars
          const displayName =
            typeof value === "string" && value.length > 22
              ? value.slice(0, 22) + "…"
              : value;
          return (
            <span
              style={{
                width: 300,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              title={value}
            >
              <>
                <img
                  src={params.row.profileUrl || defaultProfileIcon}
                  alt={value}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginRight: 8,
                    background: "#f0f0f0",
                    border: "1px solid #eee",
                  }}
                />
                <span title={value}>{displayName}</span>
              </>
            </span>
          );
        }

        // Default renderingf
        return (
          <span
            style={{
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              verticalAlign: "middle",
            }}
            title={typeof value === "string" ? value : ""}
          >
            {value === null || value === undefined || value === ""
              ? "-"
              : value}{" "}
          </span>
        );
      },
    }));

    // Add the Basic Details column
    cols.splice(1, 0, {
      field: "basicDetails",
      headerName: "Basic Details",
      sortable: false,
      width: 200,
      renderCell: (params: any) => {
        const age =
          params.row.age !== undefined && params.row.age !== null
            ? params.row.age
            : "-";
        const gender = params.row.gender
          ? params.row.gender.toLowerCase().startsWith("f")
            ? "F"
            : params.row.gender.toLowerCase().startsWith("m")
              ? "M"
              : params.row.gender.charAt(0).toUpperCase()
          : "-";
        const location = params.row.location || "-";
        return (
          <span
            style={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",

              display: "inline-block",
              verticalAlign: "middle",
            }}
            title={`${age} | ${gender} | ${location}`}
          >
            {age}
            <span className={styles.basicDetailsSeparator}>|</span>
            {gender}
            <span className={styles.basicDetailsSeparator}>|</span>
            {location}
          </span>
        );
      },
    });

    cols.push({
      field: "menu",
      headerName: "",
      width: 50,
      pinned: "right",
      renderCell: (params: any) => (
        <div className={styles.menuColumnCell}>
          <KebabMenu
            items={getOptions(
              params.row.id,
              params.row,
              handleAddReview,
              handleOpenSwap,
              handleSendInvoice,
            )}
            menuContainerClassname={styles.menuTooltip}
          />
        </div>
      ),
    });

    return cols;
  };
  // Event Handlers
  const handleTabChange = (count: number, status: string) => {
    const kpiIndex = kpis.findIndex((item) => item.label === status);
    const kpi = kpis[kpiIndex];
    if (kpi) {
      setSelectedKpiFilter({
        kpiCategory: kpi.kpiCategory,
        kpiFilter: kpi.kpiFilter,
      });
      setCurrentPage(1);
      setActiveTab(kpiIndex);
    }
  };

  const handleRowClick = (row: any) => {
    if (row.id && row.programId) {
      navigate(
        `/admin/action-cards/registered/seeker-details/${row.id}/${row.programId}`,
      );
    }
  };

  const handleAddReview = (row: any) => {
    setSelectedRow(row);
    setActiveOverlayType("review");
    const ratingsObj: { [key: string]: { rating: number; id?: number } } = {};
    if (Array.isArray(row.ratings)) {
      row.ratings.forEach((r: any) => {
        ratingsObj[r.ratingKey] = { rating: r.rating, id: r.id };
      });
    }
    setReviewInitialRatings(ratingsObj);
    setReviewInitialComments(row.rmComments || "");
  };

  const handleSaveReview = (result: { success: boolean; message?: string }) => {
    setActiveOverlayType(null);
    setSelectedRow(null);
  };
  const onRefresh= () => {
    fetchSeekersList();
  }
  const handleOpenSwap = async (row: any) => {
    setSelectedRow(row);
    setActiveOverlayType("swap");
    try {
      const response = await getCall(`${endPoints.program}/${row.programId}`);
      const data = response?.data?.data;
      const allocatedId = await fetchAllocatedProgramId(row.id);
      let options: { value: number; label: string; disabled?: boolean }[] = [];

      if (data?.type?.isGroupedProgram) {
        options =
          data.groupedPrograms?.map((item: any) => ({
            value: Number(item.id),
            label: item.name,
            disabled: Number(item.id) === allocatedId,
          })) || [];
      } else {
        options =
          data.sessions?.map((item: any) => ({
            value: Number(item.id),
            label: item.name,
            disabled: Number(item.id) === allocatedId,
          })) || [];
      }
      setDynamicProgramOptions(options);
    } catch {
      setDynamicProgramOptions([]);
    }
  };

  const handleSendInvoice = (seekerId: any) => {
    if (seekerId) {
      getCall(`invoice/sendto/${seekerId}`);
      getCall(`invoice/sendto/${seekerId}`);
    }
  };

  const downloadInvoice = async (url: string) => {
    console.log(url, "url");
    if (!url) return;
    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "invoice.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert("Failed to download invoice.");
    }
  };

  const handleRemoveFilter = (key: string, valueToRemove: string) => {
    setAppliedFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      if (Array.isArray(updatedFilters[key])) {
        updatedFilters[key] = updatedFilters[key].filter(
          (v: string) => v !== valueToRemove,
        );
        if (updatedFilters[key].length === 0) {
          delete updatedFilters[key];
        }
      } else if (typeof updatedFilters[key] === "object") {
        // Handle range object like numberOfHdbs
        delete updatedFilters[key];
      } else {
        delete updatedFilters[key];
      }

      return updatedFilters;
    });
  };

  const handleClearAllFilters = () => {
    setAppliedFilters({});
    setShowFilterOverlay(false)
  };

  const fetchSeekersList = async () => {
    setLoading(true);
    try {
      let url = `${endPoints.registeredSeekerList}?limit=${pageSize}&offset=${(currentPage - 1) * pageSize}`;
      if (programIdNumber) url += `&programId=${programIdNumber}`;

      const filtersObj: any = {};
      if (sessionId && !hasSyncedTab) {
        if (sessionId === "onHold") {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "onHold";
        } else if (["waitlisted", "yet-to-decide"].includes(sessionId)) {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "waitlisted";
        } else if (!isNaN(Number(sessionId))) {
          filtersObj.kpiCategory = "programs";
          filtersObj.kpiFilter = `program_${sessionId}`;
        }
      } else if (selectedKpiFilter) {
        filtersObj.kpiCategory = selectedKpiFilter.kpiCategory;
        filtersObj.kpiFilter = selectedKpiFilter.kpiFilter;
      }

      Object.assign(filtersObj, appliedFilters);

      if (searchValue.value.trim()) {
        url += `&searchText=${encodeURIComponent(searchValue.value.trim())}`;
      }

      url += `&filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;

      const res = await getCall(url);
      const rawData = res?.data?.data?.data || [];
      const tableHeaders = res?.data?.data?.tableHeaders || [];
      const pagination = res?.data?.data?.pagination || {};
      const kpis = res?.data?.data?.kpis || [];
      const dashboardFilter = res?.data?.data?.filters || {};
      setData(rawData);
      setColumns(buildDynamicColumns(tableHeaders));
      setTotalData(pagination.totalRecords || 0);
      setKpis(kpis);
      setDashboardFilters(dashboardFilter);
    } catch {
      setData([]);
      setColumns([]);
      setTotalData(0);
      setKpis([]);
    } finally {
      setLoading(false);
    }
  };

  // 2. Add a useEffect to sync tab from filters ONLY ONCE after kpis are loaded
  useEffect(() => {
    if (
      !hasSyncedTab &&
      filters &&
      filters.kpiCategory &&
      filters.kpiFilter &&
      kpis.length > 0
    ) {
      const tabIndex = kpis.findIndex(
        (k: any) =>
          k.kpiCategory === filters.kpiCategory &&
          k.kpiFilter === filters.kpiFilter,
      );
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
        setSelectedKpiFilter({
          kpiCategory: kpis[tabIndex].kpiCategory,
          kpiFilter: kpis[tabIndex].kpiFilter,
        });
        setHasSyncedTab(true);
      }
    }
  }, [filters, kpis, hasSyncedTab]);

  // Effects
  useEffect(() => {
    fetchSeekersList();
  }, [
    currentPage,
    pageSize,
    searchQuery,
    selectedKpiFilter,
    activeTab,
    appliedFilters,
  ]);

  useEffect(() => {
    if (!sessionId || kpis.length === 0 || hasSyncedTab) return;

    let kpiIndex = -1;

    if (sessionId === "onHold") {
      kpiIndex = kpis.findIndex((k: any) => k.kpiFilter === "onHold");
    } else if (["waitlisted", "yet-to-decide"].includes(sessionId)) {
      kpiIndex = kpis.findIndex((k: any) => k.kpiFilter === "waitlisted");
    } else if (!isNaN(Number(sessionId))) {
      kpiIndex = kpis.findIndex(
        (k: any) => k.kpiFilter === `program_${sessionId}`,
      );
    }

    if (
      kpiIndex !== -1 &&
      (activeTab !== kpiIndex ||
        !selectedKpiFilter ||
        selectedKpiFilter.kpiFilter !== kpis[kpiIndex].kpiFilter)
    ) {
      setActiveTab(kpiIndex);
      setSelectedKpiFilter({
        kpiCategory: kpis[kpiIndex].kpiCategory,
        kpiFilter: kpis[kpiIndex].kpiFilter,
      });
      setHasSyncedTab(true);
    }
  }, [sessionId, kpis]);

  // When user manually changes tab, allow auto-sync again if sessionId changes
  useEffect(() => {
    setHasSyncedTab(false);
  }, [sessionId]);

  // Render logic
  const popup = showFilterOverlay && (
    <div className={styles.overlayBackdrop}>
      <div className={styles.overlayPopup}>
        <button onClick={() => setShowFilterOverlay(false)}>Close</button>
        <AdminFilterOverlay
          displayFilters={dashboardFilters}
          open={showFilterOverlay}
          onClose={() => setShowFilterOverlay(false)}
          onApply={(filters) => {
            const cleanedFilters = Object.fromEntries(
              Object.entries(filters).filter(([_, value]) => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === "string") return value.trim() !== "";
                if (typeof value === "object") return value !== null;
                return true;
              }),
            );

            setAppliedFilters(cleanedFilters);
            setShowFilterOverlay(false);
            setCurrentPage(1);
          }}
          initialFilters={appliedFilters}
        />
      </div>
    </div>
  );

  const handleDownload = async () => {
    setShowExportPopup(false);
    try {
      let url = `${endPoints.registeredSeekerList}?limit=${pageSize}&offset=${(currentPage - 1) * pageSize}`;
      if (programIdNumber) url += `&programId=${programIdNumber}`;
      url += `&downloadType=${selectedOption}`;

      let filtersObj = {};
      if (selectedOption === "filtered") {
        filtersObj = { ...appliedFilters };
      }
      url += `&filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;

      // Get the downloadUrl from the same API
      const response = await getCall(url);
      const downloadUrl = response?.data?.data?.downloadUrl;

      if (downloadUrl) {
        await downloadExcelFromApi({
          url: downloadUrl,
          filePrefix: "Registrations",
        });
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const exportPopup = showExportPopup && (
    <DownloadPopup
      open={showExportPopup}
      onClose={() => setShowExportPopup(false)}
      selected={selectedOption}
      setSelected={setSelectedOption}
      onDownload={handleDownload}
    />
  );

  const transformedStatsData = kpis.map((item: any) => ({
    status: item.label,
    count: typeof item.value === "number" ? item.value : 0,
  }));

  return (
    <div className={styles.container}>
      {loading ? (
        <Loader type="large" />
      ) : (
        <div className={styles.dataGridContainer}>
          <AdminKpiData
            className={styles.adminKpiData}
            handleTabChange={handleTabChange}
            activeTab={activeTab}
            tabsData={transformedStatsData}
            role="admin"
            setActiveTab={setActiveTab}
          />

          <DashboardHeader
            searchValue={searchValue}
            title=""
            enableFilter={true}
            appliedFilters={appliedFilters}
            setSearchValue={setSearchValue}
            onSearch={(val) => {
              setSearchQuery(val);
              setCurrentPage(1);
            }}
            enableExport={true}
            onExport={() => setShowExportPopup(true)}
            onSearchValueChange={(value: string) =>
              setSearchValue((prev) => ({ ...prev, value }))
            }
            onFilterClick={() => setShowFilterOverlay(true)}
            filterPills={
              <FilterPills
                appliedFilters={appliedFilters}
                totalData={totalData}
                handleRemoveFilter={handleRemoveFilter}
                handleClearAllFilters={handleClearAllFilters}
                onMoreClick={() => setShowFilterOverlay(true)}
              />
            }
          />
          {exportPopup}
          {popup}
          <DataGridWithPagination
            headers={columns}
            seekersData={data}
            totalData={totalData}
            pageSize={pageSize}
            setPageSize={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
            onRowClick={handleRowClick}
            isheight={true}
            rowHeight={60}
          />
          {activeOverlayType === "review" && (
            <ReviewOverlay
              open={true}
              onClose={() => setActiveOverlayType(null)}
              onSave={() => handleSaveReview}
              seekerId={selectedRow?.id}
              programId={programIdNumber}
              initialRatings={reviewInitialRatings}
              initialComments={reviewInitialComments}
              seekersData={selectedRow}
              disablePreferences={true}
              onRefresh={ onRefresh }
            />
          )}
          {activeOverlayType === "swap" && (
            <SwapFilterOverlay
              open={true}
              swapTypeOptions={swapTypeOptions}
              programOptions={dynamicProgramOptions}
              seekerId={selectedRow?.id ? Number(selectedRow?.id) : undefined}
              initialData={selectedRow}
              onUpdate={() => setActiveOverlayType(null)}
              onCancel={() => setActiveOverlayType(null)}
              isLoading={loading}
              seekerData={selectedRow}
            />
          )}
          {showBlessCard && (
            <MahatriaBlessCard
              open={showBlessCard}
              onClose={() => setShowBlessCard(false)}
              seekerName={blessUser}
              programRegId={selectedRow?.id ? selectedRow?.id : undefined}
              programPreferences={dynamicProgramOptions}
              profileUrl={selectedRow?.profileUrl}
              // handleBless={handleBless}
              subProgramsData={subProgramsData}
              onBlessSuccess={() => {
                setShowBlessCard(false);
                fetchSeekersList();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HdbSeekersListAdmin;
